import 'babel-polyfill'

require('intersection-observer')
import moment from 'moment'
import VueLazyload from 'vue-lazyload'
import Vue from 'vue'
import VueMeta from 'vue-meta'
import VuePlyr from 'vue-plyr'
import Vue2TouchEvents from 'vue2-touch-events'

import VeeValidate from 'vee-validate'
import VueObjectFit from 'vue-object-fit'
import 'vue-object-fit/dist/vue-object-fit.css'

import {BootstrapVue} from 'bootstrap-vue'
import PortalVue from 'portal-vue'
import VueNoty from 'vuejs-noty'
import AirbnbStyleDatepicker from 'vue-airbnb-style-datepicker'
import SentryManager from "@/domain/core/utils/sentryManager";

import {i18n, getBrowserLanguage, setI18nLanguage, getLocaleConf} from './i18n'

import '@/styles/app.scss'
import './registerServiceWorker'
import smoothscroll from 'smoothscroll-polyfill';
import router from "@/router";
import Icon from "@/domain/icon/Icon.vue";
// Smooth scroll behaviour polyfill
smoothscroll.polyfill()

Vue.config.productionTip = false
// Vue.config.devtools = true
// Vue.config.performance = true

//Vue.prototype.appVars = window.appVars


const currentLanguage = getBrowserLanguage()

// Sentry
const sentryManager = new SentryManager('https://80c0554f7d5702cc0e4a3e35835224a3@sentry.getpica.com/13')
Vue.prototype.$sentry = sentryManager


Vue.component('Icon', Icon);

// Touch events
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(Vue2TouchEvents, {
    disableClick: false,
    touchClass: '',
    tapTolerance: 10,
    touchHoldTolerance: 400,
    swipeTolerance: 30,
    longTapTimeInterval: 400,
    namespace: 'touch'
})

// Lazy image
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VueLazyload)

// object-fit polyfill
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VueObjectFit)


// Bootstrap
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(BootstrapVue, {
    BToast: // override default toast options
        {
            headerClass: 'd-none',
            toastClass: 'custom-toast',
            variant: 'primary',
            autoHideDelay: 3000,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
            appendToast: true
        }
})
Vue.use(PortalVue)


// VueMeta
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VueMeta)

// VuePlyr
// The second argument is optional and sets the default config values for every player
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VuePlyr, {
    plyr: {
        fullscreen: {enabled: true}
    },
    emit: ['ended']
})

// VeeValidate
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VeeValidate, {
    events: 'change'
})
setI18nLanguage(currentLanguage)


// Noty
// ---------------------------------------------------------------------------------------------------------------------
Vue.use(VueNoty, {
    theme: 'bootstrap-v4',
    container: '.noty-custom-container',
    //layout: 'topCenter',
    timeout: 2000,
    // animation: {
    //     open : 'animated bounceInRight',
    //     close: 'animated bounceOutRight'
    // }
})


// vue-airbnb-style-datepicker
// ---------------------------------------------------------------------------------------------------------------------
const datepickerOptions = {
    days: i18n.t('datepicker.days'),
    daysShort: i18n.t('datepicker.days_short'),
    monthNames: i18n.t('datepicker.month_names'),
    sundayFirst: getLocaleConf(currentLanguage).sundayFirstDayWeek,
    width: 500,
    colors: {
        selected: '#1867FF',
        inRange: '#1867FF',
        selectedText: '#fff',
        text: '#8f8f9d',
        disabled: 'transparent',
        hoveredInRange: '#1867FF'
    }
}
Vue.use(AirbnbStyleDatepicker, datepickerOptions)

// Filters
// ---------------------------------------------------------------------------------------------------------------------
// https://www.samanthaming.com/tidbits/30-how-to-format-currency-in-es6
const numberFormat = new Intl.NumberFormat(currentLanguage, {
    style: 'currency',
    currency: 'EUR'
})

Vue.filter('currency', function (value) {
    return numberFormat.format(value)
})


Vue.filter('formatDate', function (value, format = 'L') {
    moment.locale(i18n.locale)
    if (value) {
        return moment(String(value)).format(format)
    }
})

Vue.filter('formatDateTime', function (value) {
    moment.locale(i18n.locale)
    if (value) {
        return moment(String(value)).format('LLL')
    }
})

Vue.filter('truncate', function (value, maxLength) {
    if (value) {
        return value.slice(0, maxLength) + (maxLength < value.length ? '...' : '')
    }
    return ''
})

Vue.filter('formatSecondsToHHMMSS', function (value) {
    const secs = Number.parseInt(value)
    let hours = Math.floor(secs / 3600)
    let minutes = Math.floor((secs - (hours * 3600)) / 60)
    let seconds = secs - (hours * 3600) - (minutes * 60)
    const padZero = (n) => n < 10 ? '0' + n : '' + n
    return (hours > 0 ? padZero(hours) + ':' : '') + padZero(minutes) + ':' + padZero(seconds);
})

const currenciesWithSameSymbol = {
    '$': ['USD', 'CAD', 'AUD', 'NZD', 'SGD', 'HKD', 'MXN', 'XCD'], // Dollar sign
    '¥': ['JPY', 'CNY'],                                           // Yen/Yuan
    '£': ['GBP', 'EGP', 'SYP', 'SDG'],                            // Pound
    'Rs': ['PKR', 'LKR', 'NPR'],                                  // Rupee
    '₣': ['CHF', 'XOF', 'XAF'],                                   // Franc
    '﷼': ['IRR', 'OMR', 'QAR', 'YER'],                            // Riyal/Rial
    'kr': ['SEK', 'NOK', 'DKK', 'ISK'],                           // Krona/Krone
    '₡': ['CRC', 'SVC']                                           // Colon
}

// Convert to an array of objects
const currencyList = Object.entries(currenciesWithSameSymbol).map(([symbol, currencies]) => ({
    symbol,
    currencies
}))

// Currencies without minor units (e.g. JPY, KRW): no thousands separator, otherwise "2,000" looks like a decimal
const isZeroDecimalCurrency = (currency) => new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency
}).resolvedOptions().maximumFractionDigits === 0
Vue.filter('formatCurrency', function (value, currency) {
    return new Intl.NumberFormat(currentLanguage, {
        style: 'currency',
        currency: currency,
        useGrouping: !isZeroDecimalCurrency(currency),
        currencyDisplay: currencyList.find(c => c.currencies.includes(currency)) ? 'code' : 'symbol',
        maximumFractionDigits: 2
    }).format(value)
})


// Global mixin
// ---------------------------------------------------------------------------------------------------------------------
const mixin = {
    computed: {},
    methods: {
        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        async emitPromised(method, ...params) {
            let listener = this.$listeners[method] || this.$attrs[method] || this[method]
            if (listener) {
                let res = await listener(...params)
                return res === undefined || res
            }
            return false
        },
        getCurrencySymbol(currency) {
            return (0).toLocaleString(undefined, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).replace(/\d/g, '').trim();
        }
    }
}
Vue.mixin(mixin)

function log(...args) {
    if (process.env.NODE_ENV === 'development') {
        if (args.length === 1) {
            console.log(JSON.parse(JSON.stringify(args[0])))
        } else {
            if (typeof args[0] === 'string') console.group(args.shift())
            else console.group('Log')
            for (let arg of args) {
                console.log(JSON.parse(JSON.stringify(arg)))
            }
            console.groupEnd()
        }
    }
}

window.log = log

export {Vue, i18n}